import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Search, UserCheck, X } from "lucide-react";
import { toast } from "sonner";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { fmtR } from "./orderStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyItem = () => ({ description: "", qty: 1, price: "" });
const emptyForm = () => ({
  customer_id: null, customer_name: "", customer_phone: "", items: [emptyItem()],
  deposit_paid: "", supplier_name: "", expected_date: "", notes: "",
});

const CustomerPicker = ({ form, setForm }) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const q = form.customer_name.trim();
    if (form.customer_id || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/customer-orders/customer-lookup`, { params: { q } });
        setResults(res.data || []);
        setOpen(true);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [form.customer_name, form.customer_id]);

  const pick = (c) => {
    setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name, customer_phone: f.customer_phone || c.whatsapp_number || c.contact_number || "" }));
    setOpen(false);
  };

  return (
    <div className="relative">
      <Label className="text-sm">Customer name *</Label>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input className="pl-8 pr-8" value={form.customer_name} placeholder="Search a customer or type any name"
          onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value, customer_id: null }))}
          onBlur={() => setTimeout(() => setOpen(false), 200)} data-testid="order-customer-name-input" />
        {form.customer_id && (
          <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
            onClick={() => setForm((f) => ({ ...f, customer_id: null }))} title="Unlink customer" data-testid="order-customer-unlink-btn">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {form.customer_id && <div className="mt-1 flex items-center gap-1 text-xs text-green-700" data-testid="order-customer-linked"><UserCheck className="h-3 w-3" />Linked to existing customer</div>}
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg max-h-56 overflow-auto" data-testid="order-customer-results">
          {results.map((c) => (
            <button type="button" key={c.id} onMouseDown={() => pick(c)} className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100" data-testid={`order-customer-option-${c.id}`}>
              <span className="font-medium">{c.name}</span>
              <span className="ml-2 text-xs text-gray-500">{c.account_number} {c.whatsapp_number || c.contact_number}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ItemRows = ({ items, setItems }) => {
  const update = (i, key, val) => setItems(items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500">
        <span className="col-span-6">What was ordered *</span><span className="col-span-2">Qty</span>
        <span className="col-span-3">Price each (R)</span><span className="col-span-1" />
      </div>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <Input className="col-span-6" value={it.description} onChange={(e) => update(i, "description", e.target.value)} placeholder="e.g. 6m IBR sheet 0.47" data-testid={`order-item-desc-${i}`} />
          <Input className="col-span-2" type="number" min="0" step="any" value={it.qty} onChange={(e) => update(i, "qty", e.target.value)} data-testid={`order-item-qty-${i}`} />
          <Input className="col-span-3" type="number" min="0" step="0.01" value={it.price} onChange={(e) => update(i, "price", e.target.value)} placeholder="0.00" data-testid={`order-item-price-${i}`} />
          <Button type="button" variant="ghost" size="icon" className="col-span-1 text-red-500" disabled={items.length === 1}
            onClick={() => setItems(items.filter((_, idx) => idx !== i))} data-testid={`order-item-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, emptyItem()])} data-testid="order-add-item-btn">
        <Plus className="h-4 w-4 mr-1" />Add item
      </Button>
    </div>
  );
};

export const OrderTicketDialog = ({ open, onOpenChange, ticket, onSaved }) => {
  const [form, setForm] = useState(emptyForm());
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(ticket ? { ...emptyForm(), ...ticket, items: ticket.items.map((i) => ({ ...i })), deposit_paid: ticket.deposit_paid || "", expected_date: ticket.expected_date || "" } : emptyForm());
    if (suppliers.length === 0) axios.get(`${API}/customer-orders/supplier-names`).then((r) => setSuppliers(r.data || [])).catch(() => {});
  }, [open, ticket]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = form.items.reduce((s, i) => s + (parseFloat(i.qty) || 0) * (parseFloat(i.price) || 0), 0);
  const deposit = parseFloat(form.deposit_paid) || 0;

  const handleSave = async () => {
    const items = form.items.filter((i) => i.description.trim()).map((i) => ({ description: i.description, qty: parseFloat(i.qty) || 0, price: parseFloat(i.price) || 0 }));
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    if (items.length === 0) return toast.error("Add at least one item");
    const payload = {
      customer_id: form.customer_id, customer_name: form.customer_name.trim(), customer_phone: form.customer_phone,
      items, deposit_paid: deposit, supplier_name: form.supplier_name, expected_date: form.expected_date || null, notes: form.notes,
    };
    setSaving(true);
    try {
      const res = await saveWithRetry(() => (ticket ? axios.put(`${API}/customer-orders/${ticket.id}`, payload) : axios.post(`${API}/customer-orders`, payload)));
      toast.success(ticket ? "Ticket updated" : `Ticket ${res.data.ticket_no} opened`);
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to save ticket"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-ticket-dialog">
        <DialogHeader><DialogTitle>{ticket ? `Edit Ticket ${ticket.ticket_no}` : "New Customer Order Ticket"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <CustomerPicker form={form} setForm={setForm} />
            <div>
              <Label className="text-sm">Phone (optional)</Label>
              <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="e.g. 082 123 4567" data-testid="order-customer-phone-input" />
            </div>
          </div>
          <ItemRows items={form.items} setItems={(items) => setForm({ ...form, items })} />
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-sm">Deposit paid (R)</Label>
              <Input type="number" min="0" step="0.01" value={form.deposit_paid} onChange={(e) => setForm({ ...form, deposit_paid: e.target.value })} placeholder="0.00" data-testid="order-deposit-input" />
            </div>
            <div>
              <Label className="text-sm">Supplier</Label>
              <Input list="order-supplier-list" value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="Who are we ordering from?" data-testid="order-supplier-input" />
              <datalist id="order-supplier-list">{suppliers.map((s) => <option key={s} value={s} />)}</datalist>
            </div>
            <div>
              <Label className="text-sm">Date expected</Label>
              <Input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} data-testid="order-expected-date-input" />
            </div>
          </div>
          <div>
            <Label className="text-sm">Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Colour, size, delivery address, who took the order..." data-testid="order-notes-input" />
          </div>
          <div className="flex justify-end gap-6 rounded-md bg-gray-50 px-4 py-2 text-sm" data-testid="order-totals">
            <span>Total: <strong data-testid="order-total-value">{fmtR(total)}</strong></span>
            <span>Deposit: <strong>{fmtR(deposit)}</strong></span>
            <span>Balance due: <strong className="text-orange-600" data-testid="order-balance-value">{fmtR(total - deposit)}</strong></span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="order-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="order-save-btn">{saving ? "Saving..." : ticket ? "Update Ticket" : "Open Ticket"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
